import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm, useWatch } from "react-hook-form";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { Column } from "primereact/column";
import { FormSkeleton, Header } from "../../components";
import { Button } from "iuvity-ui-components/dist/button";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import {
  MultiContentDialog,
  MultiContentDialogProps,
} from "../../components/dialog/MultiContentDialog";
import { TableHeader } from "../../components/datatable/TableHeader";
import { useApplications } from "../../hooks/useApplications";
import { useTenants } from "../../hooks/useTenants";
import { useEnvironments } from "../../hooks/useEnvironments";
import { useVersions } from "../../hooks/useVersions";
import { NewApplication, Tenant } from "../../interfaces";
import { AppContext } from "../../context/index";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { DeleteApplication } from "../../components/applications/DeleteApplication";
import { formatDateToLocale } from "../../helpers/formatDate";
import { PrivilegeValidatorComponent } from "../../components/shared/PrivilegeValidatorComponent";
import noResultsImage from "../../assets/Illustration_NoResults.svg";
import { EmptyTemplate } from "../../components/datatable/EmptyTemplate";



type ApplicationActionTypes = "CREATE" | "DELETE";

const formBlankValues = {
  name: '',
  tenantname: [],
  segment: [],
  currentversion: [],
  installedversion: [],
  status: '',
  createdat: '',
  updatedat: '',
};

export const AdminApplicationsPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    t,
    appState: { language, user, pageFilter, selectedApplication, permissiondInfoByRole },
    setAppState,
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
        name: pageFilter?.name || '',
        tenantname: pageFilter?.tenantname || [],
        segment: pageFilter?.segment || [],
        currentversion: pageFilter?.currentversion || [],
        installedversion: pageFilter?.installedversion || [],
        status: pageFilter?.status || '',
        createdat: pageFilter?.createdat || '',
        updatedat: pageFilter?.updatedat || '',
      }
      : formBlankValues,
  });

  const {
    data: applicationsData,
    applicationAction,
    reloadFn,
  } = useApplications("DATA");

  const { data: tenantsData } = useTenants("DATA");
  const { data: envsData } = useEnvironments("DATA");
  const { data: versionsData } = useVersions("DATA");

  const [visibleModalDeleteApp, setVisibleModalDeleteApp] = useState<boolean>(false);

  const { control, setValue } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const tenantsValues = useWatch({
    control,
    name: "tenantname",
  });

  const segmentsValues = useWatch({
    control,
    name: "segment",
  });

  const currentversionValues = useWatch({
    control,
    name: "currentversion",
  });

  const installedversionValues = useWatch({
    control,
    name: "installedversion",
  });

  const statusValues = useWatch({
    control,
    name: "status",
  });

  const createdatValues = useWatch({
    control,
    name: "createdat",
  });

  const updatedatValues = useWatch({
    control,
    name: "updatedat",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [mcModalProps, setMcModalProps] = useState<MultiContentDialogProps>({
    children: null,
  } as MultiContentDialogProps);

  const columns = useMemo(() => {
    return [
      {
        field: "name",
        header: t("Applications.nameLabel"),
      },
      {
        field: "segment",
        header: t("Applications.segmentLabel"),
      },
      {
        field: "currentversion",
        header: t("Applications.currentversionLabel"),
      },
      {
        field: "status",
        header: t("Applications.statusLabel"),
      },
      {
        field: "createdat",
        header: t("Applications.createdatLabel"),
      },
      {
        field: "updatedat",
        header: t("Applications.updatedatLabel"), 
      },
      {
        field: "actions",
        header: t("Applications.actionsLabel"),
      },
    ];
  }, [t]);

  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    segment: { value: null, matchMode: FilterMatchMode.IN },
    tenantname: { value: null, matchMode: FilterMatchMode.IN },
    currentversion: { value: null, matchMode: FilterMatchMode.IN },
    installedversion: { value: null, matchMode: FilterMatchMode.IN },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    createdat: { value: null, matchMode: FilterMatchMode.IN },
    updatedat: { value: null, matchMode: FilterMatchMode.IN },
  };

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [application, setApplication] = useState<number>(0);

  const [filteredData, setFilteredData] = useState<any[] | null>(null);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState(defaultFilterValues);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const formOptions = useMemo(() => {
    if (applicationsData && tenantsData && envsData && versionsData) {
      return {
        applications: applicationsData
          .reduce(function (result: any, element: any) {
            if (
              ![
                ...new Set(result.map((tenantData: any) => tenantData.value)),
              ].includes(element.appid)
            ) {
              result.push({
                label: element.name,
                value: element.id,
                tenantid: element.tenantid,
              });
            }
            return result;
          }, [])
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        segments: applicationsData
          .reduce(function (result: any, element: any) {
            if (
              ![
                ...new Set(result.map((segmentData: any) => segmentData.value)),
              ].includes(element.segment)
            ) {
              result.push({
                label: element.segment,
                value: element.segment,
              });
            }
            return result;
          }, [])
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        createdat: applicationsData
        .reduce(function (result: any, element: any) {
          if (
            ![
              ...new Set(result.map((createdatData: any) => createdatData.value)),
            ].includes(element.segment)
          ) {
            result.push({
              label: !!element?.createdat ? formatDateToLocale(element?.createdat, language, true) : '',
              value: !!element?.createdat ? formatDateToLocale(element?.createdat, language, true) : '',
            });
          }
          return result;
        }, [])
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        updatedat: applicationsData
        .reduce(function (result: any, element: any) {
          if (
            ![
              ...new Set(result.map((updatedatData: any) => updatedatData.value)),
            ].includes(element.segment)
          ) {
            result.push({
              label: !!element?.updatedat ? formatDateToLocale(element?.updatedat, language, true) : '',
              value: !!element?.updatedat ? formatDateToLocale(element?.updatedat, language, true) : '',
            });
          }
          return result;
        }, [])
        .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        tenants: tenantsData.filter((tenant: any) => tenant.status !== 'DELETED')
          .map((tenant: any) => ({
            label: tenant.name,
            value: tenant.id,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        environments: envsData
          .map((env: any) => ({
            label: env.name,
            value: env.id,
            applicationid: env.applicationid,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
        versions: versionsData
          .map((version: any) => ({
            label: version.name,
            value: version.name,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
      };
    } else {
      return;
    }
  }, [applicationsData, tenantsData, envsData, versionsData]);

  const onEditAction = (app: NewApplication) => {
    setAppState((prev) => ({
      ...prev,
      selectedApplication: app,
    }));
    navigate("/applications/edit");
  };
  const onCloneAction = (app: NewApplication) => {
    setAppState((prev) => ({
      ...prev,
      selectedApplication: app,
    }));
    navigate("/applications/new");
  };

  const onDeleteAction = async (app: NewApplication) => {
    await applicationAction("DELETE", app.id, app?.name ?? undefined);
    reloadFn();
  };

  const onClearFilters = () => {
    setFilters(defaultFilterValues);
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(formBlankValues);
  };

  const handleModalProps = async (
    type: ApplicationActionTypes,
    app?: NewApplication | null
  ) => {
    switch (type) {
      case "DELETE":
        await onDeleteAction(app!);
        setAppState((prev) => ({
          ...prev,
          selectedApplication: null,
        }));
        setVisibleModalDeleteApp(false);
        return;
      default:
        return;
    }
  };

  useLayoutEffect(() => {
    if (applicationsData && pageFilter) {
      setFilteredData(
        applicationsData.filter(
          (item: any) =>
            (form.getValues("tenantname").length > 0
              ? form.getValues("tenantname").includes(item.tenantid)
              : true) &&
            (form.getValues("segment").length > 0
              ? form.getValues("segment").includes(item.segment)
              : true) &&
            (form.getValues("currentversion").length > 0
              ? form.getValues("currentversion").includes(item.currentversion)
              : true) &&
            (form.getValues("installedversion").length > 0
              ? form
                .getValues("installedversion")
                .includes(item.installedversion)
              : true)
        )
      );
    } else if (applicationsData) {
      setFilteredData(applicationsData);
    }
  }, [applicationsData, setFilteredData, form, pageFilter]);

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Applications.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };

  const dataDynamic = (type: string) => {
    if (type === 'name') {
      return filteredData
        ?.filter((application: any) => application?.status !== 'DELETED')
        ?.map((app: any) => ({ label: app.name, value: app.name }))
        ?.sort((a: any, b: any) => a.label.localeCompare(b.label));
    }
    if (type === 'tenantname') {
      return formOptions?.tenants
    }
    if (type === 'segment') {
      return formOptions?.segments
    }
    if (type === 'currentversion' || type === 'installedversion') {
      return formOptions?.versions
    }
    if (type === 'createdat') {
      return formOptions?.createdat
    }
    if (type === 'updatedat') {
      return formOptions?.updatedat
    }
  };
  const filterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        // rules={ApplicationCreatorSchema(t).name}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-1">
                {t(`Applications.${options.field}Label`)}
              </label>
              <MultiSelect
                id={`select-widget`}
                display="chip"
                options={dataDynamic(options.field)}
                optionLabel={'label'}
                itemTemplate={ItemsTemplate}
                placeholder={t(`Applications.${options.field}Placeholder`)}
                className="mb-2"
                emptyFilterMessage={t("Applications.noAvailableMessage")}
                filter
                {...field}
              />
            </>
          );
        }}
      />
    )
  };

  const statuses = [
    `${t("Applications.activeStatusLabel")}`,
    `${t("Applications.inactiveStatusLabel")}`,
    `${t("Applications.draftStatusLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  };
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Applications.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Applications.${options.field}Placeholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  };
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Applications.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            nameValues.length > 0 ||
            segmentsValues.length > 0 ||
            tenantsValues.length > 0 ||
            currentversionValues.length > 0 ||
            installedversionValues.length > 0 ||
            statusValues.length > 0 ||
            createdatValues.length > 0 ||
            updatedatValues.length > 0
          )
        }
        onClick={options.filterApplyCallback}
      />
    )
  };
  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo', 'activa'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div className="flex align-items-center gap-3">
            <div>
              {t("Applications.applicationAdminTitle")}
            </div>
            <i
              className="iu iu-icon-help"
              style={{ color: 'var(mainColor)' }}
            />
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Applications.applicationsBreadcrumbLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Applications.applicationsBreadcrumbLabel"),
              disabled: true
            },
            {
              label: t("Applications.applicationAdminTitle"),
              style: { fontWeight: "bold" },
              disabled: true
            },

          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span dangerouslySetInnerHTML={{ __html: t("Applications.applicationAdminDescription1") }} style={{ lineHeight: 'var(--space-m)' }} />
        </div>
      </TransactionalSubheader>

      <>
        <BoxFull className="ml-0 mt-0 mx-0 px-0 dddd" variant="tertiary" style={{ paddingBottom: "1rem", paddingTop: '1rem' }}>

          <SubheaderActions
            className="px-0 pt-0 pb-0 container-buttons-app "
            style={{ marginRight: '0px !important' }}
            actions={[
              {
                id: "111",
                icon: "iu iu-icon-plus",
                label: t("Applications.newApplicationTitle"),
                style: {
                  display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                },
                onClick: () => {
                  setAppState((prev) => ({
                    ...prev,
                    selectedApplication: null,
                  }));
                  navigate(`${location.pathname}/new`)
                },
              },
            ]}
          />
        </BoxFull>
        <BoxFull className="dt-c my-0 mx-0 ml-4 " id='wrapper-applications' style={{ marginLeft: '5rem !important' }} >
          {filteredData && formOptions ? (
            <DataTable
              header={
                <TableHeader
                  title={t("Applications.availableApplications")}
                  columns={columns}
                  selectedColumns={selectedColumns}
                  toggleColumns={setSelectedColumns}
                  filters={filters}
                  setFilters={setFilters}
                  reload={reloadFn}
                  onClearFiltersColumn={onClearFilters}
                  globalFilterValue={globalFilterValue}
                />
              }
              currentPageReportTemplate={t(
                "ConsoleTranslations.rowsPaginatorTemplateLabel"
              )}
              paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
              emptyMessage={<EmptyTemplate image={noResultsImage} text={t('Applications.emptyDataTableMessage')} />}
              rows={10}
              size="normal"
              filters={filters}
              sortField="id"
              dataKey="id"
              sortOrder={1}
              value={filteredData?.filter((application: any) => application?.status !== 'DELETED').map((application: any) => ({
                ...application,
                tenantname:
                  tenantsData.find((tenant: Tenant) => application.tenantid === tenant.id)
                    ?.name || "",
                status: t(`Applications.${application.status.toLowerCase()}StatusLabel`),
                createdat: !!application?.createdat ? formatDateToLocale(application?.createdat, language, true) : '',
                updatedat: !!application?.updatedat ? formatDateToLocale(application?.updatedat, language, true) : '',
                actions: (
                  <div className="flex justify-content-center gap-2">
                    <PrivilegeValidatorComponent showButton={permissiondInfoByRole.UPDATE} >
                      <Button
                        icon="iu"
                        className="p-button-rounded p-button-info p-button-outlined act-btn"
                        aria-label="Edit"
                        tooltip={t("TableActions.editLabel")}
                        tooltipOptions={{ position: "bottom" }}
                        variant="p-button"
                        onClick={() => onEditAction(application)}
                        style={{
                          color: "var(--warningColor)",
                        }}
                      >
                        <i className="iu iu-icon-edit act-btn edit" />
                      </Button>
                    </PrivilegeValidatorComponent>
                    <PrivilegeValidatorComponent showButton={permissiondInfoByRole.REMOVE} >
                      <Button
                        icon="iu"
                        className="p-button-rounded p-button-info p-button-outlined act-btn"
                        aria-label="Delete"
                        tooltip={t("TableActions.deleteLabel")}
                        tooltipOptions={{ position: "bottom" }}
                        variant="p-button"
                        onClick={() => {
                          setAppState((prev) => ({
                            ...prev,
                            selectedApplication: application,
                          }));
                          setVisibleModalDeleteApp(true);
                        }}
                        style={{
                          color: "var(--errorColor)",
                        }}
                      >
                        <i className="iu iu-icon-delete act-btn delete" />
                      </Button>
                    </PrivilegeValidatorComponent>
                  </div>

                ),
              }))}
              scrollable
              resizableColumns
              globalFilterFields={['name', 'segment', 'tenantname', 'currentversion', 'installedversion', 'status', 'createdat', 'updatedat']}
              filterDisplay="menu"
              stateStorage="session"
              stateKey="filters-table"
            >
              {selectedColumns.map((col) => {
                return (
                  <Column
                    key={col.field}
                    field={col.field}
                    header={col.header}
                    sortable={!(col.field === "actions")}
                    align={col.field === "actions" ? "center" : "left"}
                    headerStyle={col.field === "actions"
                      ? {
                          justifyContent: "center",
                          display: (!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined,
                        }
                      : undefined}
                    style={{
                      boxShadow: (col.field === "actions")
                        ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                        : undefined,
                      maxWidth: (col.field === "actions") ? "132px" : undefined,
                      whiteSpace: "normal",
                      minWidth: (col.field === "actions") ? "10rem" : "12rem",
                      display: (col.field === "actions")
                        ? ((!permissiondInfoByRole.UPDATE && !permissiondInfoByRole.REMOVE) ? 'none' : undefined)
                        : undefined,
                    }}
                    frozen={col.field === "actions" ? true : false}
                    alignFrozen={col.field === "actions" ? "right" : undefined}
                    filterField={col.field}
                    showFilterMatchModes={false}
                    showFilterMenu={filteredData?.filter((application: any) => application?.status !== 'DELETED')?.length > 0}
                    filterMenuStyle={{ width: '23rem' }}
                    filter={col.field === "actions" ? false : true}
                    filterElement={
                      ((col.field !== "status") && filterTemplate) ||
                      (col.field === "status" && statusFilterTemplate)
                    }
                    body={(rowData) => {
                      if (col.field == 'status') {
                        return statusBodyTemplate(rowData)
                      }
                      return rowData[col.field]
                    }}
                    filterApply={filterApplyTemplate}
                    filterClear={filterClearTemplate}
                  />
                );
              })}
            </DataTable>
          ) : (
            <FormSkeleton length={8} />
          )}
        </BoxFull>
      </>

      <MultiContentDialog children={mcModalProps.children} />

      <CustomDialog
        title={modalProps.title}
        visible={modalProps.visible}
        content={modalProps.content}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
      <DeleteApplication
        visible={visibleModalDeleteApp}
        closeHandler={() => { setVisibleModalDeleteApp(false) }}
        confirmHandler={() => handleModalProps("DELETE", selectedApplication)}
        dataAppSeleted={selectedApplication}
      />
    </>
  );
};
